// The host the page runs in: a browser, or a native shell (Apps/Windows, `design-docs/Windows app — design.md` §1) that
// loads this same page from create.fiverooms.app and lends it a few things a browser can't. The page asks for a
// capability, never for a platform, so a future shell (Android, Linux) or a browser that gains an API uses the same
// code; in a browser every `has` is false and the page behaves as it always has.
//
// The protocol, version 1:
//   page → shell  `window.__TAURI_INTERNALS__.invoke(command, payload, options)`, only on the origins the shell's
//                 capability file names:
//                   `host_info` → `{ protocol, shell, version, capabilities }`
//                   `save_file` raw body = the bytes; headers `x-name` (URI-encoded), `x-mime` → `'saved' | 'cancelled'`
//                   (a JSON array of the bytes is taken too: Tauri's postMessage fallback sends one)
//                   `close_reply` `{ unsent }` → nothing (the shell then closes, or asks first)
//                   `open_external` `{ url }` → nothing: an http, https or mailto address, opened by the system
//                   `opened_files` → `OpenedItem[]`: what the writer opened with the app since the page last asked, each
//                   once (a `.scrivx` stands for its project folder, listed); never a path
//                   `read_opened` `{ id, entry? }` → raw bytes: an opened file, or one listed file of an opened folder
//   shell → page  `window.__fiveRoomsHost.closeRequested()`, run by the shell when the writer closes the window; the
//                 page answers with `close_reply`. `window.__fiveRoomsHost.filesOpened()`, run when the writer opens a
//                 file with the app while it runs; the page then asks `opened_files`.
// A shell that answers `host_info` with another protocol is treated as a browser, so an old shell never meets a page it
// can't serve, and a capability the page doesn't know is ignored.

export const HOST_PROTOCOL = 1;

/** Content Security Policy violations since the page loaded (the host self-check shows them; a shell's injected
 *  scripts are the likely cause in a native window). Kept from the first import, which App makes before any page. */
export interface PolicyViolation {
  directive: string;
  blocked: string;
}

const violations: PolicyViolation[] = [];

if (typeof document !== 'undefined') {
  document.addEventListener('securitypolicyviolation', (e) => {
    if (violations.length < 50) violations.push({ directive: e.effectiveDirective, blocked: e.blockedURI || e.sample || '(inline)' });
  });
}

export const policyViolations = (): readonly PolicyViolation[] => violations;

/** What a shell may lend the page. */
export type Capability = 'files.save' | 'close.guard' | 'links.open' | 'files.open';

/** A file or folder the writer opened with the app (Explorer's Open, Open With, a drop on its icon). */
export interface OpenedItem {
  /** The shell's name for it, for `readOpened`. */
  id: string;
  /** Its own name (`Novel.scriv`, `Draft.docx`), never its path. */
  name: string;
  kind: 'file' | 'folder';
  /** A file's size, or a folder's files' sizes together. */
  size: number;
  /** A folder's files, relative to it, `/`-separated. */
  entries?: { path: string; size: number }[];
  /** Why a folder couldn't be listed; nothing of it can be read then. */
  error?: string;
}

export interface HostInfo {
  protocol: number;
  /** `windows`, `macos`, `linux`… for the self-check and support only; never branched on. */
  shell: string;
  version: string;
  capabilities: string[];
}

/** What became of a file the page saved: `downloaded` = the browser's (or the webview's) own download, no dialog. */
export type SaveResult = 'saved' | 'cancelled' | 'downloaded';

export interface Host {
  /** null in a browser. */
  readonly info: HostInfo | null;
  has(capability: Capability): boolean;
  /** Saves bytes as a file: the shell's Save dialog (`saved` or `cancelled`), else the browser's download (`downloaded`). */
  saveFile(name: string, bytes: Uint8Array, mime: string): Promise<SaveResult>;
  /** What closing the window would lose: batches the relay hasn't acknowledged. Replaced by each call; null clears it. */
  setUnsentCounter(count: (() => Promise<number>) | null): void;
  /** Opens a web or mail address in the system's browser or mail app; false when the host can't (a browser: the page
   *  opens it itself). */
  openOutside(url: string): Promise<boolean>;
  /** What the writer opened with the app since the last call (each item once); empty when the host can't open files. */
  takeOpened(): Promise<OpenedItem[]>;
  /** An opened file's bytes, or (with `entry`) one of an opened folder's listed files. */
  readOpened(id: string, entry?: string): Promise<Uint8Array>;
  /** Called when the writer opens something with the app while the page runs. Returns the remover. */
  onFilesOpened(listener: () => void): () => void;
}

/**
 * The address a click on this link should open outside the app, or null to leave the click alone: web and mail
 * addresses that open a new window (`target=_blank`), go to another origin, or are mail. Links within the app, object
 * URLs and the like stay as they are.
 */
export function outsideLink(href: string, target: string, here: string): string | null {
  let url: URL;
  try {
    url = new URL(href, here);
  } catch {
    return null;
  }
  if (url.protocol === 'mailto:') return url.href;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return target === '_blank' || url.origin !== new URL(here).origin ? url.href : null;
}

/**
 * Work to finish before the window closes (an open project storing what was typed since its last batch), run before the
 * unsent count is read. Returns the remover. Each task gets two seconds; the window closes regardless.
 */
const closeTasks = new Set<() => Promise<unknown>>();

export function addCloseTask(task: () => Promise<unknown>): () => void {
  closeTasks.add(task);
  return () => closeTasks.delete(task);
}

const CLOSE_TASK_LIMIT = 2_000;

async function runCloseTasks() {
  await Promise.all([...closeTasks].map((task) => Promise.race([task().catch(() => {}), new Promise((r) => setTimeout(r, CLOSE_TASK_LIMIT))])));
}

interface TauriInternals {
  invoke(command: string, payload?: unknown, options?: { headers?: Record<string, string> }): Promise<unknown>;
}

type HostWindow = Window & {
  __TAURI_INTERNALS__?: TauriInternals;
  __fiveRoomsHost?: { closeRequested(): Promise<void>; filesOpened(): void };
};

/** A command's raw bytes: an ArrayBuffer over Tauri's IPC, a JSON array of numbers over its postMessage fallback. */
function bytesOf(answer: unknown): Uint8Array {
  if (answer instanceof ArrayBuffer) return new Uint8Array(answer);
  if (answer instanceof Uint8Array) return answer;
  if (Array.isArray(answer)) return Uint8Array.from(answer as number[]);
  throw new Error('The app sent something that isn’t a file.');
}

/** The browser's download: an object URL the page's policy allows, revoked a minute later. */
export function downloadInBrowser(name: string, bytes: Uint8Array, mime: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

class BrowserHost implements Host {
  readonly info = null;
  has() {
    return false;
  }
  async saveFile(name: string, bytes: Uint8Array, mime: string) {
    downloadInBrowser(name, bytes, mime);
    return 'downloaded' as const;
  }
  setUnsentCounter() {}
  async openOutside() {
    return false;
  }
  async takeOpened() {
    return [];
  }
  async readOpened(): Promise<Uint8Array> {
    throw new Error('Nothing was opened with the app.');
  }
  onFilesOpened() {
    return () => {};
  }
}

class ShellHost implements Host {
  private unsent: (() => Promise<number>) | null = null;
  private readonly openedListeners = new Set<() => void>();

  constructor(
    readonly info: HostInfo,
    private readonly tauri: TauriInternals,
    win: HostWindow,
  ) {
    win.__fiveRoomsHost = {
      closeRequested: () => this.closeRequested(),
      filesOpened: () => this.openedListeners.forEach((listener) => listener()),
    };
    if (this.has('links.open')) this.catchOutsideLinks(win);
  }

  /** In the shell, a click on an outside link is sent to `open_external` instead of WebView2's new window (in the phase
   *  0 spike a new window for a `target=_blank` link opened nothing). Middle clicks too. */
  private catchOutsideLinks(win: HostWindow) {
    const doc = (win as Window).document;
    if (!doc) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || (e.type === 'click' && e.button !== 0) || (e.type === 'auxclick' && e.button !== 1)) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const url = outsideLink(a.getAttribute('href') ?? '', a.target, win.location.href);
      if (!url) return;
      e.preventDefault();
      void this.openOutside(url);
    };
    doc.addEventListener('click', onClick);
    doc.addEventListener('auxclick', onClick);
  }

  async openOutside(url: string) {
    if (!this.has('links.open')) return false;
    try {
      await this.tauri.invoke('open_external', { url });
      return true;
    } catch {
      return false;
    }
  }

  async takeOpened(): Promise<OpenedItem[]> {
    if (!this.has('files.open')) return [];
    const items = await this.tauri.invoke('opened_files');
    return Array.isArray(items) ? (items as OpenedItem[]) : [];
  }

  async readOpened(id: string, entry?: string) {
    return bytesOf(await this.tauri.invoke('read_opened', entry === undefined ? { id } : { id, entry }));
  }

  onFilesOpened(listener: () => void) {
    this.openedListeners.add(listener);
    return () => this.openedListeners.delete(listener);
  }

  has(capability: Capability) {
    return this.info.capabilities.includes(capability);
  }

  async saveFile(name: string, bytes: Uint8Array, mime: string): Promise<SaveResult> {
    if (!this.has('files.save')) return browserHost.saveFile(name, bytes, mime);
    try {
      const answer = await this.tauri.invoke('save_file', bytes, { headers: { 'x-name': encodeURIComponent(name), 'x-mime': mime } });
      return answer === 'saved' ? 'saved' : 'cancelled';
    } catch {
      return browserHost.saveFile(name, bytes, mime); // the shell couldn't write it: the webview's own download still can
    }
  }

  setUnsentCounter(count: (() => Promise<number>) | null) {
    this.unsent = count;
  }

  private async closeRequested() {
    let unsent = 0;
    try {
      await runCloseTasks();
      unsent = this.unsent ? await this.unsent() : 0;
    } catch {
      unsent = 0; // the store couldn't be read: nothing the page can report, so closing isn't held up
    }
    await this.tauri.invoke('close_reply', { unsent });
  }
}

const browserHost = new BrowserHost();

/** Finds the host once: a shell answering this protocol, else the browser. */
export async function detectHost(win: HostWindow | undefined = typeof window === 'undefined' ? undefined : (window as HostWindow)): Promise<Host> {
  const tauri = win?.__TAURI_INTERNALS__;
  if (!win || !tauri || typeof tauri.invoke !== 'function') return browserHost;
  try {
    const info = (await tauri.invoke('host_info')) as HostInfo;
    if (!info || info.protocol !== HOST_PROTOCOL || !Array.isArray(info.capabilities)) return browserHost;
    return new ShellHost(info, tauri, win);
  } catch {
    return browserHost; // a webview that isn't ours, or a page the shell grants no IPC
  }
}

let current: Promise<Host> | null = null;
let found: Host | null = null;

/** The page's host, found on first use. */
export function host(): Promise<Host> {
  current ??= detectHost().then((h) => (found = h));
  return current;
}

/** The host if it's been found already (App asks at start), for code that must act within a click. */
export const currentHost = (): Host | null => found;

/** Saves bytes through the host (the one way the app saves a file). */
export async function saveFile(name: string, bytes: Uint8Array, mime: string): Promise<SaveResult> {
  return (await host()).saveFile(name, bytes, mime);
}
